import { useCallback, useEffect, useState } from "react";
import { api, errorText, type LogsPullResult } from "../api";
import { gunzipB64 } from "../download";

// Log-Puffer + Live-Stream. Datensparsamkeit: der EventSource lebt nur,
// solange `streaming` an UND der Browser-Tab sichtbar ist — beim Unmount
// (Tab-Wechsel in der Detailseite) räumt der Effekt ihn sofort ab.

const MAX_LINES = 2000;

export function useLogStream(deviceId: string) {
  const [streaming, setStreaming] = useState(false);
  const [tabVisible, setTabVisible] = useState(() => !document.hidden);
  const [lines, setLines] = useState<string[]>([]);
  const [pulling, setPulling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gzFallback, setGzFallback] = useState<string | null>(null);

  const append = useCallback((text: string) => {
    // Ankommende Zeilen sind der Beweis, dass der Stream steht — ein Abbruch
    // von vorhin darf dann nicht mehr angezeigt werden. Hier statt beim Aufbau
    // im Effekt, denn dort wäre es ein Zustandswechsel im Renderlauf.
    setError(null);
    setLines((prev) => {
      const next = prev.concat(text.split("\n").filter((l) => l !== ""));
      return next.length > MAX_LINES ? next.slice(next.length - MAX_LINES) : next;
    });
  }, []);

  // Browser-Tab im Hintergrund → Stream pausieren (bares LTE-Volumen).
  useEffect(() => {
    const onVisibility = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  useEffect(() => {
    if (!streaming || !tabVisible) return;
    // Ein SSE-Event kann mehrere Zeilen tragen (der Agent bündelt ~1 s).
    return api.streamLogs(deviceId, append, () =>
      setError("Log-Stream abgebrochen — Router offline oder Verbindung gestört."),
    );
  }, [streaming, tabVisible, deviceId, append]);

  /** Snapshot der letzten Zeilen holen (`logs_pull`). */
  const pull = useCallback(async () => {
    setPulling(true);
    setError(null);
    setGzFallback(null);
    try {
      const result = await api.pullLogs(deviceId, 500);
      const data: LogsPullResult = result.data;
      if (data.text !== undefined) {
        setLines(data.text.split("\n").slice(-MAX_LINES));
      } else if (data.gz_b64 !== undefined) {
        // Fallback-Pfad: der Agent hat gzip geschickt.
        try {
          setLines((await gunzipB64(data.gz_b64)).split("\n").slice(-MAX_LINES));
        } catch {
          setGzFallback(data.gz_b64);
          setError("Dieser Browser kann das gzip-Paket nicht entpacken — bitte herunterladen.");
        }
      }
    } catch (e) {
      setError(errorText(e));
    } finally {
      setPulling(false);
    }
  }, [deviceId]);

  return {
    lines,
    maxLines: MAX_LINES,
    streaming,
    live: streaming && tabVisible,
    paused: streaming && !tabVisible,
    toggle: () => {
      setError(null);
      setStreaming((s) => !s);
    },
    clear: () => setLines([]),
    pull,
    pulling,
    error,
    clearError: () => setError(null),
    gzFallback,
  };
}
